import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import { api } from "@/lib/api";

export default function ConsentReview() {
  const { bid } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";

  const [state, setState] = useState({ loading: true, err: "", data: null });
  const [answer, setAnswer] = useState(null); // "yes" | "no" | null
  const [notes, setNotes] = useState("");
  const [signature, setSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // "reconfirmed" | "awaiting_admin_review"

  useEffect(() => {
    api.get(`/consent-review/${bid}`, { params: { token } })
      .then((r) => {
        setState({ loading: false, err: "", data: r.data });
        // If already submitted, prefill state
        const r0 = r.data?.review;
        if (r0?.status === "reconfirmed") setDone("reconfirmed");
        else if (r0?.status === "awaiting_admin_review") setDone("awaiting_admin_review");
      })
      .catch((e) => setState({ loading: false, err: e?.response?.data?.detail || "Invalid link", data: null }));
  }, [bid, token]);

  const submit = async () => {
    if (answer === null) return;
    if (answer === "no" && !signature.trim()) {
      toast.error("Please sign to reconfirm your consent");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/consent-review/${bid}`, {
        medical_changed: answer === "yes",
        changes_notes: notes,
        signature_name: signature.trim(),
      }, { params: { token } });
      setDone(r.data.status);
      toast.success(r.data.status === "reconfirmed" ? "Consent reconfirmed — thank you"
        : "Thank you — CLAÈ will review your update shortly");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit your review");
    }
    setSubmitting(false);
  };

  if (state.loading) return (<>
    <Header />
    <main className="max-w-2xl mx-auto px-6 pt-10 pb-24">
      <p className="mono-label">Loading your appointment details…</p>
    </main>
  </>);

  if (state.err) return (<>
    <Header />
    <main className="max-w-2xl mx-auto px-6 pt-10 pb-24">
      <p className="mono-label mb-3">CLAÈ · CONSENT REVIEW</p>
      <h1 className="font-serif text-3xl">{state.err}</h1>
    </main>
  </>);

  const b = state.data.booking;
  const pkg = state.data.package;

  if (done === "reconfirmed") return (<>
    <Header />
    <main className="max-w-2xl mx-auto px-6 pt-10 pb-24" data-testid="consent-reconfirmed">
      <p className="mono-label mb-3">CLAÈ · CONSENT REVIEW</p>
      <h1 className="font-serif text-4xl">Consent reconfirmed</h1>
      <p className="mt-4 text-stone-600 text-sm sm:text-base">
        Thank you. We'll see you on <strong>{b.date} at {b.time}</strong>.
        You'll receive a normal 24-hour appointment reminder closer to the date.
      </p>
    </main>
  </>);

  if (done === "awaiting_admin_review") return (<>
    <Header />
    <main className="max-w-2xl mx-auto px-6 pt-10 pb-24" data-testid="consent-awaiting-admin">
      <p className="mono-label mb-3">CLAÈ · CONSENT REVIEW</p>
      <h1 className="font-serif text-4xl">Thank you</h1>
      <p className="mt-4 text-stone-600 text-sm sm:text-base">
        We've received your medical update and will review it before your session on
        <strong> {b.date} at {b.time}</strong>. A member of the CLAÈ team may contact
        you to confirm whether your treatment can go ahead as planned.
      </p>
      <p className="mt-3 text-stone-500 text-xs">
        You will not be asked to sign consent again until we've reviewed and cleared the change.
      </p>
    </main>
  </>);

  return (<>
    <Header />
    <main className="max-w-2xl mx-auto px-6 pt-10 pb-24" data-testid="consent-review-page">
      <p className="mono-label mb-3">CLAÈ · CONSENT REVIEW</p>
      <h1 className="font-serif text-4xl sm:text-5xl font-light">Quick medical review</h1>

      <div className="mt-6 border hairline bg-white p-4 text-sm text-stone-700">
        <p><span className="mono-label text-stone-500">TREATMENT</span> · {(b.items || []).map((i) => i.name).join(", ")}</p>
        <p className="mt-1"><span className="mono-label text-stone-500">DATE</span> · {b.date} at {b.time}</p>
        {pkg && <p className="mt-1"><span className="mono-label text-stone-500">COURSE</span> · {pkg.course_name} ({pkg.sessions_completed}/{pkg.total_sessions} completed)</p>}
      </div>

      <div className="mt-8">
        <p className="text-lg text-stone-900">Has any medical information changed since your previous treatment?</p>
        <div className="flex gap-3 mt-4">
          <button data-testid="med-yes" onClick={() => setAnswer("yes")}
                  className={`border hairline px-6 py-3 mono-label ${answer === "yes" ? "bg-stone-900 text-white" : "hover:bg-alt"}`}>YES</button>
          <button data-testid="med-no" onClick={() => setAnswer("no")}
                  className={`border hairline px-6 py-3 mono-label ${answer === "no" ? "bg-stone-900 text-white" : "hover:bg-alt"}`}>NO</button>
        </div>
      </div>

      {answer === "yes" && (
        <div className="mt-8">
          <label className="mono-label text-stone-500 text-xs">
            Please tell us what has changed (medication, health condition, allergies, pregnancy, etc.)
          </label>
          <textarea
            data-testid="changes-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-2 w-full border hairline bg-white p-3 text-sm min-h-[140px] focus:outline-none focus:border-stone-900"
            placeholder="Describe the change in your own words…" />
          <button data-testid="submit-changes" onClick={submit} disabled={submitting}
                  className="mt-4 bg-stone-900 text-white px-8 py-3 mono-label disabled:opacity-40">
            {submitting ? "Sending…" : "Send to CLAÈ for review"}
          </button>
          <p className="text-xs text-stone-500 mt-3">
            We will pause your consent process until a practitioner has reviewed your update.
          </p>
        </div>
      )}

      {answer === "no" && (
        <div className="mt-8">
          <p className="text-lg text-stone-900 mb-3">Consent reconfirmed?</p>
          <p className="text-sm text-stone-600 mb-4">
            I confirm that no relevant medical information has changed since I signed my Session 1 consent,
            and I reconfirm my consent to the treatment above.
          </p>
          <label className="mono-label text-stone-500 text-xs">SIGN — TYPE YOUR FULL NAME</label>
          <input
            data-testid="signature"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            className="mt-2 w-full border hairline bg-white p-3 text-sm focus:outline-none focus:border-stone-900"
            placeholder="e.g. Katie Bob" />
          <button data-testid="submit-reconfirm" onClick={submit} disabled={submitting}
                  className="mt-4 bg-stone-900 text-white px-8 py-3 mono-label disabled:opacity-40">
            {submitting ? "Submitting…" : "Reconfirm consent"}
          </button>
        </div>
      )}
    </main>
  </>);
}
